import moment from "moment";
import i18n from "@/i18n";

export interface DashboardOfferRow {
  statuscode?: string;
  internalcode?: string;
  commercialname?: string | null;
  familyname?: string;
  firstname?: string;
  clientreference?: string;
  offerchannel?: string;
  siren?: string | null;
  outstandingbalancebefore?: number | string;
  saleschannelcode?: string;
  sales_channel_code?: string;
  financialprofile?: string;
  financial_profile?: string;
  financedvalue?: number | string;
  financed_value?: number | string;
  whencreated?: string;
  whenmodified?: string;
  _when_created?: string;
  businessdata?: string;
  _discriminator?: string;
}

export interface DashboardKpiCard {
  total: number;
  byChannel: Record<string, number>;
}

export interface DashboardStatusSlice {
  key: string;
  label: string;
  count: number;
  color: string;
}

export interface DashboardChannelStatus {
  channel: string;
  channelLabel: string;
  slices: DashboardStatusSlice[];
}

export interface DashboardFinancingBar {
  key: string;
  label: string;
  count: number;
  amount: number;
  color: string;
}

export interface DashboardMonthlySeries {
  key: string;
  label: string;
  color: string;
}

export interface DashboardMonthlyPoint {
  month: string;
  values: Record<string, number>;
  amounts: Record<string, number>;
}

export interface DashboardTopClient {
  key: string;
  label: string;
  reference: string;
  count: number;
  amount: number;
}

export interface DashboardConversionKpi {
  rate: number;
  contractedCount: number;
  totalCount: number;
}

export interface DashboardMetrics {
  totalSimulations: DashboardKpiCard;
  physicalPersons: DashboardKpiCard;
  legalEntities: DashboardKpiCard;
  totalAmount: {
    total: number;
    cnParc: number;
  };
  topClients: DashboardTopClient[];
  conversion: DashboardConversionKpi;
  statusByChannel: DashboardChannelStatus[];
  financingTypes: DashboardFinancingBar[];
  monthlyEvolution: DashboardMonthlyPoint[];
  monthlySeries: DashboardMonthlySeries[];
}

const CHART_COLORS = [
  "#f97316",
  "#38bdf8",
  "#22c55e",
  "#6366f1",
  "#8b5cf6",
  "#3b82f6",
  "#06b6d4",
  "#f59e0b",
  "#ec4899",
  "#ef4444",
];

const UNKNOWN_CHANNEL = "AUTRE";
const UNKNOWN_PRODUCT = "NON_RENSEIGNE";
const UNKNOWN_STATUS = "UNKNOWN";

function parseBusinessData(row: DashboardOfferRow): Record<string, any> {
  if (!row.businessdata) return {};
  try {
    return JSON.parse(row.businessdata);
  } catch {
    return {};
  }
}

export function getOfferChannel(row: DashboardOfferRow): string {
  if (row.offerchannel?.trim()) {
    return row.offerchannel.trim();
  }

  const businessData = parseBusinessData(row);
  const channel =
    businessData.additionalData?.channel ||
    businessData.channel ||
    row.saleschannelcode ||
    row.sales_channel_code;

  return channel?.toString().trim() || UNKNOWN_CHANNEL;
}

export function getFinancialProduct(row: DashboardOfferRow): string {
  const businessData = parseBusinessData(row);
  const product =
    businessData.financing?.financialProduct ||
    row.financialprofile ||
    row.financial_profile;

  return product?.toString().trim() || UNKNOWN_PRODUCT;
}

export function getOfferStatusKey(row: DashboardOfferRow): string {
  const statusCode = (row.statuscode || "").trim().toUpperCase();
  const internalCode = (row.internalcode || "").trim().toUpperCase();

  if (
    statusCode.startsWith("ANALYSIS") ||
    statusCode === "START" ||
    statusCode === "ONHOLD"
  ) {
    return "ANALYSIS";
  }

  if (internalCode) {
    return internalCode;
  }

  return row.statuscode?.trim() || UNKNOWN_STATUS;
}

export function getOfferStatusLabel(statusKey: string): string {
  if (!statusKey || statusKey === UNKNOWN_STATUS) {
    return "Non renseigné";
  }

  const translationKeys =
    statusKey === "DECISION"
      ? [
          "enum.offer.ClientTaskDefinition.STEP_ENTER_DECISION",
          "enum.offer.status.DECISION",
        ]
      : statusKey === "ANALYSIS"
      ? [
          "enum.offer.ClientTaskDefinition.ANALYSIS",
          "enum.offer.status.ANALYSIS",
        ]
      : [
          `enum.offer.status.${statusKey}`,
          `enum.offer.ClientTaskDefinition.${statusKey}`,
        ];

  for (const translationKey of translationKeys) {
    if (i18n.global.te(translationKey)) {
      return String(i18n.global.t(translationKey));
    }
  }

  return statusKey;
}

export function formatChannelLabel(channel: string): string {
  return channel.toUpperCase();
}

export function formatFinancialProductLabel(product: string): string {
  return product.replace(/\./g, " ");
}

export function isOrganizationRow(row: DashboardOfferRow): boolean {
  const businessData = parseBusinessData(row);
  const customer = businessData.parties?.find(
    (party: { role?: string }) => party.role === "CUSTOMER"
  );

  if (customer?.customerType === "Organization") return true;
  if (customer?.customerType === "Person") return false;
  if (row.commercialname?.trim()) return true;
  if (row.siren?.trim()) return true;
  if (row._discriminator?.toLowerCase().includes("organization")) return true;

  return false;
}

export function getClientKey(row: DashboardOfferRow): string {
  if (row.clientreference?.trim()) {
    return row.clientreference.trim();
  }

  if (row.commercialname?.trim()) {
    return `org:${row.commercialname.trim()}`;
  }

  const personName = [row.firstname, row.familyname]
    .filter(Boolean)
    .join("_")
    .trim();

  return personName || "UNKNOWN";
}

export function getClientLabel(row: DashboardOfferRow): string {
  if (row.commercialname?.trim()) {
    return row.commercialname.trim();
  }

  const personName = [row.firstname, row.familyname].filter(Boolean).join(" ").trim();
  if (personName) return personName;

  return row.clientreference?.trim() || "Client inconnu";
}

export function isContractedOffer(row: DashboardOfferRow): boolean {
  const internalCode = (row.internalcode || "").trim().toUpperCase();
  const statusCode = (row.statuscode || "").trim().toUpperCase();

  if (internalCode === "CONTRACT") return true;

  return [
    "CONTRACT",
    "CONTRACTSIGN",
    "CUSTACCEPT",
    "MANSIGNATURE_COMPLETED",
    "ELECSIGNATURE_COMPLETED",
  ].includes(statusCode);
}

export function getFinancedAmount(row: DashboardOfferRow): number {
  const businessData = parseBusinessData(row);
  const candidates = [
    row.outstandingbalancebefore,
    row.financedvalue,
    row.financed_value,
    businessData.financing?.financedValue,
    businessData.financing?.priceWTax,
    businessData.financedValue,
    businessData.financedvalue,
    businessData.priceWTax,
    businessData.totalAmount,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return 0;
}

export function getOfferDate(row: DashboardOfferRow): moment.Moment | null {
  const raw =
    row.whencreated ||
    row.whenmodified ||
    row._when_created ||
    parseBusinessData(row).creationDate;

  if (!raw) return null;

  const formats = [
    "YYYY-MM-DD HH:mm:ss",
    "DD/MM/YYYY HH:mm",
    "DD/MM/YYYY",
    moment.ISO_8601,
  ];

  for (const format of formats) {
    const parsed =
      format === moment.ISO_8601
        ? moment(raw)
        : moment(raw, format as moment.MomentFormatSpecification, true);
    if (parsed.isValid()) return parsed;
  }

  return null;
}

function buildKpiCard(): DashboardKpiCard {
  return { total: 0, byChannel: {} };
}

function incrementChannelCount(
  target: Record<string, number>,
  channel: string
): void {
  target[channel] = (target[channel] || 0) + 1;
}

function incrementStatusCount(
  map: Map<string, number>,
  statusKey: string
): void {
  map.set(statusKey, (map.get(statusKey) || 0) + 1);
}

const STATUS_COLOR_BY_KEY: Record<string, string> = {
  ANALYSIS: "#f97316",
  DECISION: "#38bdf8",
  INITIAL: "#22c55e",
  CONTRACT: "#6366f1",
  CANCELLED: "#94a3b8",
};

function getStatusColor(statusKey: string, index: number): string {
  return STATUS_COLOR_BY_KEY[statusKey] || CHART_COLORS[index % CHART_COLORS.length];
}

function buildStatusSlices(statusCounts: Map<string, number>): DashboardStatusSlice[] {
  return Array.from(statusCounts.entries())
    .map(([key, count], index) => ({
      key,
      label: getOfferStatusLabel(key),
      count,
      color: getStatusColor(key, index),
    }))
    .sort((a, b) => b.count - a.count);
}

export function getTopChannelEntries(
  byChannel: Record<string, number>,
  limit = 3
): Array<{ channel: string; count: number }> {
  return Object.entries(byChannel)
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function getStatusSlicePercent(count: number, total: number): number {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}

export function computePercentChange(current: number, previous: number): number | null {
  if (previous === 0) {
    return current > 0 ? 100 : null;
  }
  return Math.round(((current - previous) / previous) * 100);
}

export function formatTrendLabel(change: number | null): string {
  if (change === null) return "";
  if (change > 0) return `▲ +${change}%`;
  if (change < 0) return `▼ ${change}%`;
  return "= 0%";
}

export function getChannelStatusTotal(
  channelStatus: DashboardChannelStatus
): number {
  return channelStatus.slices.reduce((sum, item) => sum + item.count, 0);
}

export function buildDashboardMetrics(
  rows: DashboardOfferRow[]
): DashboardMetrics {
  const totalSimulations = buildKpiCard();
  const physicalPersons = buildKpiCard();
  const legalEntities = buildKpiCard();
  let totalAmount = 0;
  let cnParcAmount = 0;

  const statusByChannelMap = new Map<string, Map<string, number>>();
  const financingCounts = new Map<string, number>();
  const financingAmounts = new Map<string, number>();
  const monthlyMap = new Map<string, Record<string, number>>();
  const monthlyAmountMap = new Map<string, Record<string, number>>();
  const clientMap = new Map<
    string,
    { label: string; reference: string; count: number; amount: number }
  >();
  let contractedCount = 0;

  rows.forEach((row) => {
    const channel = getOfferChannel(row);
    const financialProduct = getFinancialProduct(row);
    const statusKey = getOfferStatusKey(row);
    const amount = getFinancedAmount(row);
    const isOrg = isOrganizationRow(row);

    totalSimulations.total += 1;
    incrementChannelCount(totalSimulations.byChannel, channel);

    if (isOrg) {
      legalEntities.total += 1;
      incrementChannelCount(legalEntities.byChannel, channel);
    } else {
      physicalPersons.total += 1;
      incrementChannelCount(physicalPersons.byChannel, channel);
    }

    totalAmount += amount;
    if (isContractedOffer(row)) {
      contractedCount += 1;
    }
    if (statusKey === "CONTRACT" || statusKey === "CONTRACTSIGN") {
      cnParcAmount += amount;
    }

    const clientKey = getClientKey(row);
    const clientEntry = clientMap.get(clientKey) || {
      label: getClientLabel(row),
      reference: row.clientreference?.trim() || "-",
      count: 0,
      amount: 0,
    };
    clientEntry.count += 1;
    clientEntry.amount += amount;
    clientMap.set(clientKey, clientEntry);

    if (!statusByChannelMap.has(channel)) {
      statusByChannelMap.set(channel, new Map<string, number>());
    }
    incrementStatusCount(statusByChannelMap.get(channel)!, statusKey);

    financingCounts.set(
      financialProduct,
      (financingCounts.get(financialProduct) || 0) + 1
    );
    financingAmounts.set(
      financialProduct,
      (financingAmounts.get(financialProduct) || 0) + amount
    );

    const offerDate = getOfferDate(row);
    if (offerDate) {
      const monthKey = offerDate.format("YYYY-MM");
      const current = monthlyMap.get(monthKey) || {};
      current[financialProduct] = (current[financialProduct] || 0) + 1;
      monthlyMap.set(monthKey, current);

      const currentAmounts = monthlyAmountMap.get(monthKey) || {};
      currentAmounts[financialProduct] =
        (currentAmounts[financialProduct] || 0) + amount;
      monthlyAmountMap.set(monthKey, currentAmounts);
    }
  });

  const statusByChannel = Array.from(statusByChannelMap.entries())
    .map(([channel, statusCounts]) => ({
      channel,
      channelLabel: formatChannelLabel(channel),
      slices: buildStatusSlices(statusCounts),
    }))
    .sort((a, b) => getChannelStatusTotal(b) - getChannelStatusTotal(a));

  const financingTypes = Array.from(financingCounts.entries())
    .map(([key, count], index) => ({
      key,
      label: formatFinancialProductLabel(key),
      count,
      amount: financingAmounts.get(key) || 0,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))
    .sort((a, b) => b.count - a.count);

  const monthlySeries = financingTypes.map((item) => ({
    key: item.key,
    label: item.label,
    color: item.color,
  }));

  const monthlyEvolution = Array.from(monthlyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([monthKey, values]) => ({
      month: moment(monthKey, "YYYY-MM").format("MMM"),
      values,
      amounts: monthlyAmountMap.get(monthKey) || {},
    }));

  const topClients = Array.from(clientMap.entries())
    .map(([key, data]) => ({
      key,
      label: data.label,
      reference: data.reference,
      count: data.count,
      amount: data.amount,
    }))
    .sort((a, b) => b.count - a.count || b.amount - a.amount)
    .slice(0, 5);

  const conversion: DashboardConversionKpi = {
    rate: totalSimulations.total
      ? Math.round((contractedCount / totalSimulations.total) * 100)
      : 0,
    contractedCount,
    totalCount: totalSimulations.total,
  };

  return {
    totalSimulations,
    physicalPersons,
    legalEntities,
    totalAmount: {
      total: totalAmount,
      cnParc: cnParcAmount,
    },
    topClients,
    conversion,
    statusByChannel,
    financingTypes,
    monthlyEvolution,
    monthlySeries,
  };
}

export function formatDashboardAmount(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function getDashboardPeriodDates(days: number): {
  from: string;
  until: string;
} {
  const until = moment();
  const from = moment().subtract(days, "days");
  return {
    from: from.format("DD/MM/YYYY"),
    until: until.format("DD/MM/YYYY"),
  };
}

export function getPreviousDashboardPeriodDates(days: number): {
  from: string;
  until: string;
} {
  const until = moment().subtract(days, "days");
  const from = moment().subtract(days * 2, "days");
  return {
    from: from.format("DD/MM/YYYY"),
    until: until.format("DD/MM/YYYY"),
  };
}
